  // src/components/gestionEvento/Ajustes/Competidores/Inscripciones/AthleteSidebar.jsx
  import React, { useEffect, useState } from 'react';
  import PropTypes from 'prop-types';
  import {
    Box,
    Drawer,
    Avatar,
    Typography,
    IconButton,
    Tooltip,
    Divider,
    Card,
    CardContent,
    Stack,
    Chip,
  } from '@mui/material';
  import {
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
  } from '@mui/icons-material';

  import PaymentStatusSection from './sidebar/PaymentStatusSection';
  import ContactSection from './sidebar/ContactSection';
  import TeamSection from './sidebar/TeamSection';
  import PruebasSection from './sidebar/PruebasSection';
  import AddressSection from './sidebar/AddressSection';
  import EmergencySection from './sidebar/EmergencySection';
  import TutorSection from './sidebar/TutorSection';
  import { createOrUpdateTeam } from '../../../../../utils/teamUtils';

  import { useInscripciones } from '../../../../../context/InscripcionesContext';

  const toText = (v, fallback = '—') => {
    if (v === null || v === undefined) return fallback;
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (typeof v === 'object') return v.nombre ?? v.name ?? v.label ?? fallback;
    return String(v ?? fallback);
  };

  const StatusBadge = ({ status }) => {
    const map = {
      Pagado: { color: 'success', label: 'Pagado' },
      EnProgreso: { color: 'primary', label: 'En Progreso' },
      Pendiente: { color: 'warning', label: 'Pendiente' },
      Rechazado: { color: 'error', label: 'Rechazado' },
    };
    const s = status || 'Pendiente';
    const cfg = map[s] || map.Pendiente;
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };
  StatusBadge.propTypes = { status: PropTypes.string };

  const formatPago = (p) => {
    if (!p) return '—';
    if (p === 'enLinea') return 'En línea';
    if (p === 'diaEvento') return 'Efectivo';
    return String(p);
  };

  function AthleteSidebar({
    open,
    width,
    member,
    categorias,
    pruebas,
    equipos,
    competition,
    onClose,
    onSave,
    setCompetencia,
  }) {
    const [edit, setEdit] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const { updateInscripcion } = useInscripciones();

    useEffect(() => {
      setEdit(member ? { ...member } : null);
      setEditMode(false);
    }, [member]);

    if (!member) {
      return (
        <Drawer
          variant="persistent"
          anchor="right"
          open={open}
          PaperProps={{
            sx: { width: width || 720, borderLeft: (t) => `1px solid ${t.palette.divider}` },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Selecciona un atleta para ver los detalles
            </Typography>
          </Box>
        </Drawer>
      );
    }

    const handleSave = async () => {
      if (!edit) return;
      await updateInscripcion(competition.id, edit.id, edit); // guarda en subcolección
      onSave?.(edit);
      setEditMode(false);
    };

    const handleTeamChange = (newTeam) => {
      if (!editMode) return;
      if (!newTeam || !newTeam.name) return;
      const { team, equiposActualizados } = createOrUpdateTeam(
        competition,
        newTeam.name,
        undefined // sin URL de logo
      );
      setEdit((p) => ({ ...p, equipo: team }));
      setCompetencia({ ...competition, equipos: equiposActualizados, id: competition.id });
    };

    const initial =
      toText(edit?.equipo?.name ?? edit?.equipo ?? edit?.nombre, '')
        .charAt(0)
        .toUpperCase();

    return (
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        PaperProps={{
          sx: {
            width: width || 720,
            borderLeft: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, position: 'relative', height: '100%', overflowY: 'auto' }}>
          {/* Cerrar */}
          <Tooltip title="Cerrar">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>

          {/* ===== Sección 1: Avatar / Nombre / Registro ===== */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Avatar
              sx={{ width: 96, height: 96, mx: 'auto', mb: 1 }}
              src={toText(edit?.fotoPerfil, '') || undefined}
            >
              {!edit?.fotoPerfil ? (initial || '') : null}
            </Avatar>

            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {`${toText(edit?.nombre, '')}${edit?.apellido ? ` ${toText(edit?.apellido, '')}` : ''}`}
            </Typography>

            {edit?.codigoId ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Registro: {toText(edit?.codigoId)}
              </Typography>
            ) : null}

            {/* Acciones */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
              {!editMode ? (
                <Tooltip title="Editar">
                  <IconButton onClick={() => setEditMode(true)} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Guardar">
                  <IconButton color="primary" onClick={handleSave} size="small">
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* ===== Sección 2: Status & Pago ===== */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              {editMode ? (
                <PaymentStatusSection value={edit} readOnly={false} onChange={setEdit} />
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Estado de Pago
                  </Typography>
                  <StatusBadge status={toText(edit?.status, 'Pendiente')} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {formatPago(edit?.pago || edit?.paymentMethod)}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* ===== Sección 3: Contacto ===== */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <ContactSection
                value={edit}
                readOnly={!editMode}
                onChange={editMode ? setEdit : undefined}
              />
            </CardContent>
          </Card>

          {/* ===== Sección 4: Equipo / Rama / Categoría ===== */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <TeamSection
                value={edit}
                readOnly={!editMode}
                onChange={editMode ? setEdit : undefined}
                categorias={categorias}
                onTeamChange={handleTeamChange}
              />
            </CardContent>
          </Card>

          {/* ===== Sección 5: Pruebas (por fila con marca) ===== */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <PruebasSection
                value={edit}
                onChange={editMode ? setEdit : undefined}
                competition={competition}
                pruebas={pruebas}
                readOnly={!editMode}
              />
            </CardContent>
          </Card>

          {/* Dirección */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <AddressSection
                value={edit}
                readOnly={!editMode}
                onChange={editMode ? setEdit : undefined}
              />
            </CardContent>
          </Card>

          {/* Emergencia */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent>
              <EmergencySection
                value={edit}
                readOnly={!editMode}
                onChange={editMode ? setEdit : undefined}
              />
            </CardContent>
          </Card>

          {/* Tutor + Asociación */}
          <TutorSection
            value={edit}
            readOnly={!editMode}
            onChange={editMode ? setEdit : undefined}
          />
        </Box>
      </Drawer>
    );
  }

  AthleteSidebar.propTypes = {
    open: PropTypes.bool,
    width: PropTypes.number,
    member: PropTypes.object,
    categorias: PropTypes.array,
    pruebas: PropTypes.array,
    equipos: PropTypes.array,
    competition: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    setCompetencia: PropTypes.func,
  };

  export default AthleteSidebar;
